/**
 * The municipality summary: what the sidebar shows before a building is picked.
 *
 * Every figure is read off the generated type by name. The previous version went
 * through a `Record<string, unknown>` cast and a hand-written `num(summary, key)`
 * helper, which let it ask for `ahorro_total`, `n_viviendas`, `letras` and two
 * demand fields the endpoint has never returned — so the panel rendered four en
 * dashes and a row of empty bars, and the compiler could not say so because the
 * cast had thrown the contract away. Read the fields directly and that class of
 * bug is a build error (SPEC §6).
 *
 * The demand figures are gone rather than fixed: /municipio/{nom}/summary does
 * not carry them for the whole stock, only per building. What replaced them is
 * what the endpoint does answer and what a prospector actually opens this panel
 * for — how much of the stock is already non-compliant, and by when.
 */

import type { MunicipioSummary } from "../api/client";
import { fmt, fmtCount, fmtPct } from "./lib/format";
import { LETTERS, LETTER_COLORS, LETTER_TEXT } from "./lib/letters";

interface Props {
  municipio: string | null;
  summary: MunicipioSummary | null;
  /** How many buildings the current filters leave visible. */
  visible: number;
  loading: boolean;
}

const DEADLINES = [
  { year: 2030, letters: "letras F y G", count: "deadline_2030_FG", pct: "pct_2030" },
  { year: 2033, letters: "letra E", count: "deadline_2033_E", pct: "pct_2033" },
  { year: 2035, letters: "letra D", count: "deadline_2035_D", pct: "pct_2035" },
] as const;

export function SummaryPanel({ municipio, summary, visible, loading }: Props) {
  if (!municipio) {
    return (
      <div className="panel-empty">
        <p className="panel-empty-title">Selecciona un municipio</p>
        <p>
          Busca arriba para explorar el parque edificatorio y descubrir potencial
          de rehabilitación.
        </p>
      </div>
    );
  }

  if (loading || !summary) {
    // aria-busy rather than a bare spinner: the panel keeps its shape, so a
    // screen reader is told the region is updating instead of reading a stale
    // summary as though it described the new municipality.
    return <div className="panel-empty" aria-busy="true">Cargando {municipio}…</div>;
  }

  // The rated stock, which is what the percentages are quoted against — not
  // total_edificios, a third of which is industrial or agricultural and has no
  // letter at all. Quoting one against the other understates every share.
  const rated = summary.total_con_datos;
  const ahorro = summary.ahorro_potencial_total;

  return (
    <div className="summary">
      <h2 className="summary-title">{municipio}</h2>
      <p className="summary-sub">
        {fmtCount(summary.total_edificios, "edificios catastrales")}
        {/* Only when a filter is actually hiding something: on an unfiltered map
            the two numbers differ anyway — one counts the stock, the other the
            polygons drawn — and printing both invites the reader to reconcile
            figures that are not meant to match. */}
        {visible < rated && ` · ${fmtCount(visible)} visibles`}
      </p>

      <div className="stat-grid">
        <div className="stat-card">
          <span className="stat-val">{fmtCount(rated)}</span>
          <span className="stat-lbl">con datos energéticos</span>
        </div>
        <div className="stat-card">
          <span className="stat-val">{fmtCount(summary.sin_datos)}</span>
          <span className="stat-lbl">industrial / agrícola</span>
        </div>
      </div>

      <h3 className="summary-heading">Distribución por letra</h3>
      <ul className="letter-bars">
        {LETTERS.map((letter) => {
          const n = summary.por_letra[letter] ?? 0;
          const pct = rated > 0 ? (n / rated) * 100 : 0;
          return (
            <li key={letter} className="letter-bar">
              <span
                className="letter-chip"
                style={{
                  background: LETTER_COLORS[letter],
                  color: LETTER_TEXT[letter],
                }}
              >
                {letter}
              </span>
              <span className="letter-track">
                {/* Width is the only place the proportion lives visually, so the
                    count is printed too — a 0.4 % bar is invisible. */}
                <span
                  className="letter-fill"
                  style={{ width: `${pct}%`, background: LETTER_COLORS[letter] }}
                />
              </span>
              <span className="letter-count">{fmtCount(n)} ({fmtPct(pct)})</span>
            </li>
          );
        })}
      </ul>

      <h3 className="summary-heading">Obligaciones EED · estimado</h3>
      {DEADLINES.map(({ year, letters, count, pct }) => (
        <div className={year === 2030 ? "deadline-item urgent" : "deadline-item"} key={year}>
          <p className="deadline-year">Antes de {year} · {letters}</p>
          <p className="deadline-desc">
            {fmtCount(summary[count])} edificios · {fmtPct(summary[pct])} del parque
          </p>
        </div>
      ))}

      <dl className="stat-list">
        <div>
          <dt>Ahorro potencial</dt>
          {/* Millions once the figure runs to seven digits, which it does for
              anything larger than a village: "12.845.300 €/año" wraps mid-number
              in a 23rem column. */}
          <dd>{ahorro >= 1e6 ? `${fmt(ahorro / 1e6, 1)} M€/año` : fmt(ahorro, 0, "€/año")}</dd>
        </div>
      </dl>

      <p className="summary-note">
        Ahorro respecto a las instalaciones CTE actuales. Estimaciones estadísticas
        de prospección: no sustituyen a un certificado de eficiencia energética.
      </p>
    </div>
  );
}
